import React from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  User, Star, BadgeCheck, Clock, Calendar
} from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { EscortProfile } from '@/types';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface ProfileHeaderProps {
  profile: EscortProfile;
  profileImage: string;
  isGeneratingImage: boolean;
  onToggleFavorite?: (profileId: string) => void;
  isFavorite?: boolean;
  onOpenBookingModal?: () => void;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ 
  profile, 
  profileImage, 
  isGeneratingImage
}) => {
  const { t } = useLanguage();

  if (isGeneratingImage) {
    return (
      <div className="relative w-full aspect-video mb-8 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
        <Skeleton className="absolute inset-0" />
        <div className="z-10 text-center">
          <p className="text-muted-foreground">{t('profile.generating_image')}</p>
        </div>
      </div>
    );
  }

  // Fonction pour formater la date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(new Date(dateString));
    } catch (e) {
      return dateString; // Fallback to original string
    }
  };
  
  // Obtenir la date d'inscription formatée
  const registrationDate = formatDate(profile.createdAt);

  return (
    <div className="mb-6">
      <div className="flex items-start gap-4">
        {/* Profile Avatar */}
        {isGeneratingImage ? (
          <Skeleton className="w-24 h-24 rounded-full" />
        ) : (
          <Avatar className="w-24 h-24 border-2 border-pink-100">
            <AvatarImage src={profileImage} alt={profile.displayName} className="object-cover" />
            <AvatarFallback>
              <User size={32} className="text-pink-500" />
            </AvatarFallback>
          </Avatar>
        )}
        
        <div className="flex-1 space-y-3">
          {/* Profile Name and Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold flex items-center">
              {profile.displayName}
              {profile.isVerified && (
                <BadgeCheck className="ml-1 w-5 h-5 text-green-500" />
              )}
            </h1>
            
            <div className="flex flex-wrap gap-1.5">
              {profile.isVerified && (
                <Badge className="bg-green-500 hover:bg-green-600">
                  {t('common:verified')}
                </Badge>
              )}
              
              {profile.isOnline && (
                <Badge className="bg-blue-500 hover:bg-blue-600 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-green-300 mr-1.5 animate-pulse"></span>
                  {t('common:online')}
                </Badge>
              )}
              
              {profile.isTouring && (
                <Badge className="bg-purple-500 hover:bg-purple-600">
                  {t('common:touring')}
                </Badge>
              )}
              
              <Badge className="bg-amber-500 hover:bg-amber-600 flex items-center gap-1">
                <Star className="h-3 w-3" />
                {profile.rating || 'N/A'}
              </Badge>
            </div>
          </div>
          
          {/* Basic Info */}
          <div className="flex items-center text-sm text-gray-500 flex-wrap gap-x-4 gap-y-1">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-1.5 text-pink-500" />
              <span>{profile.age} {t('profile.years')}</span>
            </div>
            
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1.5 text-pink-500" />
              <span>{t('common:registered_date_prefix')}: {registrationDate}</span>
            </div>
            
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1.5 text-pink-500" />
              <span>{t('common:profile.last_online')}: {t('common:today')}</span>
            </div>
            
            <div className="flex items-center">
              <User className="w-4 h-4 mr-1.5 text-pink-500" />
              <span>{t('common:total_views')}: {profile.views || 0}</span>
            </div>
          </div>
          
          {/* Bio Summary */}
          <p className="text-gray-700">
            {profile.bio?.short || t('profile.default_bio')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
